import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangleIcon, ChevronDownIcon, LockIcon } from 'lucide-react';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import type { TaxonomyFieldValue } from '@/entities/taxonomy';
import { comboboxChoices, optionLabel } from '../lib/comboboxChoices';
import type { ComboboxChoice } from '../lib/comboboxChoices';
import { MAX_CELL_VALUE_LENGTH } from '@/entities/taxonomy';

const EM_DASH = '—';
/** Sentinel for the Clear item, since a `SelectItem` may not have an empty value. */
// eslint-disable-next-line i18next/no-literal-string -- a sentinel value, never rendered
const CLEAR_VALUE = '__clear__';

/**
 * Above this many options the editor becomes searchable.
 *
 * Tessra's own guidance: `Select` is for "small static option lists without
 * search". A market or channel vocabulary runs to hundreds, where scrolling a
 * plain list is the slow path the feedback called out.
 */
const SEARCHABLE_OPTION_THRESHOLD = 10;

export interface RecordCellProps {
  value: string;
  /** Allowed values, already narrowed by the schema's rules for this record. */
  options: TaxonomyFieldValue[];
  /** A dropdown constrains; free text does not. */
  constrained: boolean;
  editable: boolean;
  /** Bound to a shared list: still selectable, but the vocabulary is not ours. */
  bound: boolean;
  selected: boolean;
  isFocus: boolean;
  /**
   * Whether this cell's editor is open.
   *
   * Owned by the grid rather than by the cell, because a double-click is no
   * longer the only way in: the grid opens the focused cell on Enter or F2, and
   * it cannot reach state that lives in here.
   */
  editing: boolean;
  /** Why this value is invalid, if it is. */
  violation?: string | null;
  /**
   * Shown, muted, when the cell is empty — the shape a value WILL take rather
   * than a value. A computed field uses it to preview the code it would mint,
   * so a person can see the pattern before spending one.
   */
  placeholder?: string;
  /** `row-col`, so keyboard navigation can find the cell and scroll it in. */
  dataCell: string;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onEditingChange: (open: boolean) => void;
  onCommit: (next: string | null) => void;
}

/**
 * One grid cell.
 *
 * CLICK TO EDIT rather than a permanently-mounted control per cell. A schema can
 * carry 40 fields, and a grid of that many open inputs is unreadable — the rule
 * `JobTableCell` states and this copies, along with its shape: an open Tessra
 * `Select` for a constrained cell, a plain input for free text, a locked span
 * when the column is derived.
 *
 * A constrained cell is the piece this feature genuinely adds. The schema
 * builder's grid renders a list-bound column READ-ONLY with "edit these options
 * in the list itself", because there it is defining the vocabulary; here the
 * point is to pick from it.
 */
export const RecordCell = memo(function RecordCell({
  value,
  options,
  constrained,
  editable,
  bound,
  selected,
  isFocus,
  editing,
  violation,
  placeholder,
  dataCell,
  onMouseDown,
  onMouseEnter,
  onEditingChange,
  onCommit,
}: RecordCellProps) {
  const { t } = useTranslation('knowledge');
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, value]);

  const background = selected ? 'bg-primary/10' : undefined;
  const ring = isFocus ? 'ring-2 ring-inset ring-primary' : undefined;

  function body() {
    if (!editable) {
      /**
       * A derived cell showing a preview rather than a value. Rendered
       * `font-mono` and dimmed so it does not read as something already
       * allocated — the distinction matters here, because the real thing costs a
       * permanent code.
       */
      if (!value && placeholder) {
        return (
          <span
            title={t('taxonomy.grid.codeDryRun')}
            /**
             * Labelled, not just dimmed. Opacity was the only thing separating a
             * preview from an allocated code, which a screen reader cannot
             * convey and a `title` on a non-focusable span never reaches by
             * keyboard or touch. The label says what it is; the italic and the
             * dimming stay as the visual cue.
             */
            aria-label={t('taxonomy.grid.codeDryRunLabel', { code: placeholder })}
            className="flex items-center gap-1 truncate px-2 font-mono italic text-muted-foreground/60"
          >
            <LockIcon className="size-3 shrink-0" aria-hidden />
            {placeholder}
          </span>
        );
      }
      return (
        <span className="flex items-center gap-1 truncate px-2 text-muted-foreground">
          <LockIcon className="size-3 shrink-0" aria-hidden />
          {value || EM_DASH}
        </span>
      );
    }

    if (editing && constrained && options.length > SEARCHABLE_OPTION_THRESHOLD) {
      /*
       * Items are OBJECTS, not label strings, and that is what makes this correct
       * rather than merely working.
       *
       * Filtering runs through `ComboboxCollection` — Base UI filters the items it
       * renders itself, so hand-mapping `<ComboboxItem>` children leaves the list
       * unfiltered while still looking like a search box. Typing then narrows
       * nothing and ArrowDown+Enter commits whatever sits first in the full list.
       *
       * The label carries both halves so typing either the name or the code finds
       * the row, but the VALUE is what travels back — mapping a chosen label back
       * to an option by string match broke two ways: a vocabulary entry literally
       * named "Clear" wiped the cell, and `Foo (BAR)` collided with `Foo` + code
       * `BAR`. A null value is the Clear sentinel, the same trick the short-list
       * `Select` path uses for the same reason.
       */
      const choices = comboboxChoices(options, value, t('taxonomy.grid.clear'));
      const selected = choices.find((choice) => choice.value === value) ?? null;

      return (
        <Combobox<ComboboxChoice>
          items={choices}
          itemToStringLabel={(choice) => choice.label}
          isItemEqualToValue={(a, b) => a.value === b.value}
          value={selected}
          onValueChange={(next: ComboboxChoice | null) => {
            onEditingChange(false);
            onCommit(next?.value ?? null);
          }}
          open
          onOpenChange={(next: boolean) => !next && onEditingChange(false)}
        >
          {/*
            A real `<input>`, and that is load-bearing rather than incidental.
            `isEditorEvent` in the grid classifies keystrokes by element TYPE
            (`instanceof HTMLInputElement`), which is what keeps the arrow keys,
            Delete and paste inside this editor from driving the sheet — and what
            makes it survive the popup being portaled outside the table. A
            `contenteditable` search box would make all three grid predicates fail
            open at once.
          */}
          <ComboboxInput
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the cell just entered edit mode; typing must land here without a second click
            autoFocus
            className="h-10 w-full border-none bg-background px-2 text-sm outline-none"
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          />
          {/*
            The popup portals out of the table, so it inherits `pointer-events: none`
            from any modal ancestor — every option renders and none accepts a click.
          */}
          {/*
            `w-max` overrides the library's `w-(--anchor-width)`, which pins the popup to the
            CELL. A narrow column then clips every option to a few characters — the reported
            bug. Bounded on both sides instead: wide enough for a real label, never wider than
            the dropdown can usefully be. `min-w-60` replaces the library's minimum, which
            is only the cell's width plus 28px and so still too narrow in a narrow column.
          */}
          <ComboboxContent className="pointer-events-auto w-max min-w-60 max-w-112">
            <ComboboxList>
              <ComboboxCollection>
                {(choice: ComboboxChoice) => (
                  <ComboboxItem key={choice.value ?? CLEAR_VALUE} value={choice}>
                    {choice.label}
                  </ComboboxItem>
                )}
              </ComboboxCollection>
            </ComboboxList>
            <ComboboxEmpty>{t('taxonomy.grid.noMatchingOptions')}</ComboboxEmpty>
          </ComboboxContent>
        </Combobox>
      );
    }

    if (editing && constrained) {
      return (
        <Select
          open
          value={value || ''}
          onValueChange={(next) => {
            onEditingChange(false);
            onCommit(next === CLEAR_VALUE ? null : next);
          }}
          onOpenChange={(open) => !open && onEditingChange(false)}
        >
          <SelectTrigger size="mini" className="w-full border-none bg-transparent px-0">
            <SelectValue placeholder={EM_DASH} />
          </SelectTrigger>
          {/*
            The searchable popup's 448px cap, but never narrower than the cell: Tessra's viewport
            is at least the trigger's width, so a lower cap would clip the options' right edge.
          */}
          <SelectContent className="max-w-[max(28rem,var(--radix-select-trigger-width))]">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {optionLabel(option)}
              </SelectItem>
            ))}
            {value && <SelectItem value={CLEAR_VALUE}>{t('taxonomy.grid.clear')}</SelectItem>}
          </SelectContent>
        </Select>
      );
    }

    if (editing) {
      return (
        <input
          ref={inputRef}
          value={draft}
          maxLength={MAX_CELL_VALUE_LENGTH}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            onEditingChange(false);
            onCommit(draft === '' ? null : draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
            if (e.key === 'Escape') {
              setDraft(value);
              onEditingChange(false);
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-10 w-full bg-background px-2 text-sm text-foreground outline-none"
        />
      );
    }

    return (
      <span
        title={violation ?? (bound ? t('taxonomy.grid.cellFromList') : undefined)}
        className="flex items-center gap-1 px-2"
      >
        <span className={`min-w-0 flex-1 truncate ${value ? '' : 'text-muted-foreground/40'}`}>
          {/* The CODE is what lands in the name, so the cell shows the code and
              the dropdown shows the label. */}
          {displayed(value, options) ||
            (placeholder ? (
              /*
               * The field's default, shown rather than stored.
               *
               * Italic so it cannot be mistaken for a value — the same treatment
               * the tracking-code preview uses, for the same reason. It is NOT
               * written into the cell: doing that would make every untouched row
               * dirty on open and would save a value nobody chose.
               *
               * This is the only way a DATE default ever surfaces. The server seeds
               * dropdown, free-text and number defaults but deliberately not date
               * (resolving one needs a clock it does not own), while the builder
               * lets someone set one anyway.
               */
              <span className="italic">{placeholder}</span>
            ) : (
              EM_DASH
            ))}
        </span>
        {violation && (
          <AlertTriangleIcon
            className="size-3.5 shrink-0 text-destructive"
            aria-label={violation}
            role="img"
          />
        )}
        {/*
          A resting chevron, so a constrained cell reads as a picker instead of as
          text. The `Select` itself still mounts only while editing — at 40 columns
          a grid of permanently open controls is unreadable, which is the rule this
          copies from `JobTableCell`.

          It opens on a SINGLE click while the cell needs a double: without its own
          handler the chevron would look like a control and do nothing on the
          gesture people actually aim at it with.

          Not in the tab order, and hidden from assistive tech. This table declares
          `role="grid"`, so the ARIA pattern puts exactly one tab stop on the
          container and moves within it by arrow key; a focusable control per cell
          would add one stop per row. The keyboard route already exists and is
          Enter/F2 on the grid. (The schema builder's problem triangle IS
          Tab-reachable — that table declares no role, where the pattern says every
          focusable element belongs in the sequence.)

          `onMouseDown` is prevented so the click does not pull focus off the grid
          container, while still bubbling to the cell's own mousedown, which is what
          selects it.
        */}
        {constrained && (
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onEditingChange(true)}
            className="flex shrink-0 items-center text-foreground-subtle"
          >
            <ChevronDownIcon className="size-4" aria-hidden />
          </button>
        )}
      </span>
    );
  }

  return (
    <td
      data-cell={dataCell}
      /**
       * `id` and `role` exist for the SCREEN READER, not for styling.
       *
       * Focus stays on the grid element (it owns arrow-key navigation), so
       * without an id for `aria-activedescendant` to point at, a cell's
       * accessible name is never announced — the grid is what has focus, and the
       * label describing the cell is unreachable by keyboard. That mattered most
       * on a tracking-code preview, where the label is the only thing
       * distinguishing a PREVIEW from an allocated code.
       */
      id={`taxonomy-cell-${dataCell}`}
      role="gridcell"
      aria-selected={selected}
      className={`h-10 border-b border-r border-border text-sm ${background ?? ''} ${ring ?? ''}`}
      /**
       * Selection is NOT editing, so it is not gated on `editable`.
       *
       * A generated column has to be selectable — copying a tracking code is the
       * main reason to click one. Gating mousedown on `editable` meant a click on
       * such a cell set nothing, and the grid's focus handler then seeded the
       * top-left cell instead: from the user's side the selection jumped to a
       * different column. Only the double-click, which opens an editor, stays
       * gated.
       */
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onDoubleClick={editable ? () => onEditingChange(true) : undefined}
    >
      {body()}
    </td>
  );
});

/** A chosen value rendered as its code, falling back to the label. */
function displayed(value: string, options: TaxonomyFieldValue[]): string {
  if (!value) {
    return '';
  }
  const match = options.find((o) => o.value === value);
  return match ? match.code || match.value : value;
}
